import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import {
  loadAdminSettings,
  saveAdminSettings,
} from "../adminPreferencesStorage";
import { fetchUserPreferences, updateUserPreferences } from "../../../lib/preferencesApi";
import { useTranslation } from "../../../i18n";

const TIMEZONES = [
  { value: "America/New_York", label: "(UTC-05:00) Eastern Time (US & Canada)" },
  { value: "Europe/London", label: "(UTC+00:00) London" },
  { value: "Europe/Berlin", label: "(UTC+01:00) Berlin" },
  { value: "Asia/Singapore", label: "(UTC+08:00) Singapore" },
];

const COMPACT_SELECT_BUTTON =
  "flex w-full items-center justify-between gap-2 rounded-lg bg-white px-3 py-2.5 text-left text-body-md text-on-surface ring-1 ring-outline-variant transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary";

function SettingsToggle({ checked, onChange, label }) {
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        onChange={onChange}
        aria-label={label}
      />
      <span
        className={[
          "relative h-6 w-11 rounded-full bg-outline-variant transition-colors",
          "after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full",
          "after:border after:border-gray-300 after:bg-white after:transition-transform",
          "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2",
          "peer-focus-visible:outline-secondary peer-checked:bg-secondary",
          "peer-checked:after:translate-x-5",
        ].join(" ")}
        aria-hidden
      />
    </label>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <div className="mb-6 flex items-center">
      <MaterialIcon name={icon} filled className="mr-3 text-[22px] text-secondary" />
      <h2 className="font-headline-md text-headline-md text-on-surface">{title}</h2>
    </div>
  );
}

const AdminSettingsPage = () => {
  const { t, setLocale } = useTranslation();
  const savedSnapshotRef = useRef(loadAdminSettings());
  const [settings, setSettings] = useState(() => loadAdminSettings());
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const remote = await fetchUserPreferences();
        if (!active || !remote || Object.keys(remote).length === 0) return;
        const merged = { ...loadAdminSettings(), ...remote };
        setSettings(merged);
        savedSnapshotRef.current = { ...merged };
        saveAdminSettings(merged);
        if (remote.language) setLocale(remote.language);
      } catch {
        /* fall back to localStorage cache */
      }
    })();
    return () => {
      active = false;
    };
  }, [setLocale]);

  const languages = useMemo(
    () => [
      { value: "de", label: t("settings.languageDe") },
      { value: "en", label: t("settings.languageEn") },
    ],
    [t]
  );

  const adminToggles = useMemo(
    () => [
      { key: "emailNotifications", label: t("adminSettings.emailNotifications"), hint: t("adminSettings.emailNotificationsHint") },
      { key: "compactTables", label: t("adminSettings.compactTables"), hint: t("adminSettings.compactTablesHint") },
      { key: "showCasePricing", label: t("adminSettings.showCasePricing"), hint: t("adminSettings.showCasePricingHint") },
      { key: "confirmImpersonation", label: t("adminSettings.confirmImpersonation"), hint: t("adminSettings.confirmImpersonationHint") },
    ],
    [t]
  );

  const resourceLinks = useMemo(
    () => [
      { label: t("adminSettings.opsDocs"), to: "/dashboard/admin/documentation", icon: "description" },
      { label: t("adminSettings.emailAlerts"), to: "/dashboard/admin/email-alerts", icon: "notifications" },
      { label: t("adminSettings.userManagement"), to: "/dashboard/admin/users", icon: "group" },
    ],
    [t]
  );

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSnapshotRef.current),
    [settings]
  );

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
    if (key === "language") setLocale(value);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await updateUserPreferences(settings);
      saveAdminSettings(settings);
      savedSnapshotRef.current = { ...settings };
      setToastVisible(true);
      window.setTimeout(() => setToastVisible(false), 3000);
    } catch {
      setSaveError(t("settings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="relative min-h-screen bg-background font-body-md text-on-surface antialiased">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute right-[-5%] top-[-10%] h-[40%] w-[40%] rounded-full bg-secondary-container/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] h-[30%] w-[30%] rounded-full bg-surface-container/30 blur-[100px]" />
      </div>

      <div className="mx-auto max-w-container-max px-margin-mobile py-12 md:px-margin-desktop">
        <nav className="mb-12">
          <Link
            to="/dashboard/admin"
            className="group inline-flex items-center text-on-primary-container transition-colors hover:text-primary"
          >
            <MaterialIcon
              name="arrow_back"
              className="mr-2 text-[20px] transition-transform group-hover:-translate-x-1"
            />
            <span className="font-label-caps text-label-caps">{t("common.backToDashboard")}</span>
          </Link>
        </nav>

        <header className="mb-12">
          <h1 className="mb-2 font-headline-xl text-headline-xl text-on-surface">
            {t("adminSettings.title")}
          </h1>
          <p className="max-w-2xl font-body-lg text-body-lg text-on-surface-variant">
            {t("adminSettings.subtitle")}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-12">
          <div className="space-y-gutter lg:col-span-8">
            <section className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 shadow-soft">
              <SectionHeader icon="language" title={t("settings.general")} />
              <div className="grid grid-cols-1 gap-stack-lg md:grid-cols-2">
                <label className="flex flex-col space-y-2">
                  <span className="font-label-sm text-label-sm text-on-surface-variant">
                    {t("settings.language")}
                  </span>
                  <EnterpriseFilterSelect
                    value={settings.language}
                    onChange={(value) => update("language", value)}
                    options={languages}
                    buttonClassName={`${COMPACT_SELECT_BUTTON} bg-surface-container-low`}
                    ariaLabel={t("settings.language")}
                  />
                </label>
                <label className="flex flex-col space-y-2">
                  <span className="font-label-sm text-label-sm text-on-surface-variant">
                    {t("settings.timezone")}
                  </span>
                  <EnterpriseFilterSelect
                    value={settings.timezone}
                    onChange={(value) => update("timezone", value)}
                    options={TIMEZONES}
                    buttonClassName={`${COMPACT_SELECT_BUTTON} bg-surface-container-low`}
                    ariaLabel={t("settings.timezone")}
                  />
                </label>
              </div>
            </section>

            <section className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 shadow-soft">
              <SectionHeader icon="admin_panel_settings" title={t("adminSettings.workspaceTitle")} />
              <div className="space-y-6">
                {adminToggles.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-6 py-2">
                    <div className="min-w-0 flex flex-col">
                      <span className="font-body-lg font-medium text-on-surface">{item.label}</span>
                      <span className="text-label-sm text-on-surface-variant">{item.hint}</span>
                    </div>
                    <SettingsToggle
                      checked={Boolean(settings[item.key])}
                      onChange={() => update(item.key, !settings[item.key])}
                      label={item.label}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 shadow-soft">
              <SectionHeader icon="security" title={t("settings.accountSecurity")} />
              <div className="flex flex-col gap-4 rounded-lg bg-surface p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center">
                  <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary-fixed-dim">
                    <MaterialIcon name="key" className="text-[20px] text-primary" />
                  </div>
                  <div>
                    <p className="font-body-md font-semibold text-on-surface">{t("settings.password")}</p>
                    <p className="text-label-sm text-on-surface-variant">{t("settings.passwordLastChanged")}</p>
                  </div>
                </div>
                <Link
                  to="/passwort-vergessen"
                  className="rounded-lg px-4 py-2 text-center text-label-sm font-semibold text-primary transition-colors hover:bg-surface-container"
                >
                  {t("settings.changePassword")}
                </Link>
              </div>
            </section>
          </div>

          <aside className="space-y-gutter lg:col-span-4">
            <div className="overflow-hidden rounded-xl bg-primary-container text-on-primary shadow-lg">
              <div className="p-6">
                <h3 className="mb-2 font-headline-md text-headline-md text-primary-fixed">
                  {t("adminHome.platformAdmin")}
                </h3>
                <p className="text-label-sm text-on-primary-container">{t("common.globalReputation")}</p>
              </div>
              <div className="relative h-48 overflow-hidden bg-surface-variant">
                <div
                  className="absolute inset-0 bg-gradient-to-br from-secondary/30 via-surface-container-high to-primary-container opacity-90"
                  aria-hidden
                />
                <div className="absolute bottom-4 left-6">
                  <div className="flex items-center space-x-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-secondary-fixed" />
                    <span className="text-label-sm font-semibold text-white">
                      {t("common.systemStatusOptimal")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-outline-variant/30 bg-surface-container p-6">
              <h4 className="mb-4 font-label-caps text-label-caps uppercase text-on-surface-variant">
                {t("adminSettings.quickLinks")}
              </h4>
              <ul className="space-y-4">
                {resourceLinks.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      className="group flex items-center text-body-md text-on-surface transition-colors hover:text-secondary"
                    >
                      <MaterialIcon
                        name={item.icon}
                        className="mr-3 text-[20px] text-on-surface-variant transition-colors group-hover:text-secondary"
                      />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <footer className="sticky bottom-8 z-20 mt-12">
          <div className="mx-auto flex max-w-container-max flex-col items-center justify-between rounded-2xl border border-outline-variant/50 bg-surface-container-lowest/80 p-4 shadow-soft backdrop-blur-md md:flex-row md:p-6">
            <div className="mb-4 flex items-center space-x-3 md:mb-0">
              <MaterialIcon name="info" className="text-[20px] text-secondary" />
              <p className="text-body-md italic text-on-surface-variant">
                {saveError
                  ? saveError
                  : hasUnsavedChanges
                    ? t("common.unsavedChanges")
                    : t("common.allSaved")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
              className="group flex items-center justify-center rounded-lg bg-primary px-10 py-3 font-bold text-white transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? (
                <>
                  <MaterialIcon name="sync" className="mr-2 animate-spin text-[18px]" />
                  {t("common.saving")}
                </>
              ) : (
                <>
                  {t("settings.saveSettings")}
                  <MaterialIcon
                    name="arrow_forward"
                    className="ml-2 text-[18px] transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
        </footer>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={[
          "pointer-events-none fixed bottom-10 right-10 z-50 flex items-center gap-3 rounded-xl bg-on-secondary-container px-6 py-4 text-white shadow-xl transition-all duration-300",
          toastVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0",
        ].join(" ")}
      >
        <MaterialIcon name="check_circle" className="text-[22px]" />
        <span className="font-body-md">{t("common.preferencesSaved")}</span>
      </div>
    </main>
  );
};

export default AdminSettingsPage;
